import React, { useState } from "react";
import "../Testing.css";
import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import {
  getSuiteMode,
  getSuiteTestingState,
} from "../../../../redux/reducers/ModuleStateReducer";
import { ESuiteMode, ETestingState } from "../../../../Model/ModuleState";
import ToolsContentButton from "../../ToolsCommon/ToolsContentButton";
import TestingApi from "../../../../api/TestingApi";
import { EArmType } from "../../../../Model/Waypoint";
import { Module, ModuleCapabilities } from "../../../../Model/Module";
import { EModuleType } from "../../../../Model/EModuleType";
import { ETestingType } from "../../../../Model/TestingParameter";
import { getModules } from "../../../../redux/reducers/ModuleReducer";
import { getModuleCapabilitiesById } from "../../../../redux/reducers/ModuleCapabilitiesReducer";
import SpinnerTextBox from "../../../Controls/SpinnerTextBox";
import TextBox from "../../../Common/TextBox";
import CheckBox from "../../../Buttons/CheckBox";
import CommonDropDown, {
  CommonDropDownDisplayValue,
} from "../../../Common/DropDown/CommonDropDown";
import { getSuiteConfiguration } from "../../../../redux/reducers/InitialConfigurationReducer";
import { EImagerType } from "../../../../Model";

interface ModuleArm {
  identifier: string;
  name: string;
  armType: EArmType;
}

interface MultiSelectData {
  id: string;
  display: string;
  selected: boolean;
}

function getModuleArms(
  modules: Module[],
  moduleCapabilitiesById: { [index: string]: ModuleCapabilities }
) {
  let moduleArms: ModuleArm[] = [];
  let armTypeCounter: { [index: number]: number } = {};
  let armStatus: MultiSelectData[] = [];

  const addToModuleArms = (module: Module, supportedArmTypes: EArmType[]) => {
    for (let i = 0; i < supportedArmTypes.length; i++) {
      const armType = supportedArmTypes[i];
      moduleArms.push({
        identifier: module.identifier,
        name: module.name,
        armType: armType,
      });
      if (armTypeCounter[armType] == null) {
        armTypeCounter[armType] = 1;
      } else {
        armTypeCounter[armType]++;
      }
    }
  };

  for (let i = 0; i < modules.length; i++) {
    const module = modules[i];
    switch (module.moduleType) {
      case EModuleType.IMAGER: {
        addToModuleArms(
          module,
          moduleCapabilitiesById[module.identifier]?.imager
            ?.supportedArmTypes ?? []
        );
        break;
      }
      case EModuleType.PSR: {
        addToModuleArms(
          module,
          moduleCapabilitiesById[module.identifier]?.psr?.supportedArmTypes ??
            []
        );
        break;
      }
      case EModuleType.CHAMBER: {
        addToModuleArms(
          module,
          moduleCapabilitiesById[module.identifier]?.chamber
            ?.supportedArmTypes ?? []
        );
        break;
      }
      case EModuleType.LOADPORT: {
        addToModuleArms(
          module,
          moduleCapabilitiesById[module.identifier]?.loadPort
            ?.supportedArmTypes ?? []
        );
        break;
      }
    }
  }

  for (let i = 0; i < moduleArms.length; i++) {
    const moduleArm = moduleArms[i];
    const appendModuleName = armTypeCounter[moduleArm.armType] > 1;
    armStatus.push({
      id: i.toString(),
      display: appendModuleName
        ? moduleArm.name + "-" + EArmType[moduleArm.armType]
        : EArmType[moduleArm.armType],
      selected: false,
    });
  }

  return {
    armStatus: armStatus,
    moduleArms: moduleArms,
  };
}

export default function TestingMovementContent() {
  const suiteMode = useTypedSelector((state) => getSuiteMode(state));
  const testingState = useTypedSelector((state) => getSuiteTestingState(state));
  const isDisabled = suiteMode !== ESuiteMode.Normal;

  const modules = useTypedSelector((state) => getModules(state));
  const moduleCapabilities = useTypedSelector((state) =>
    getModuleCapabilitiesById(state)
  );
  const { imagerType } = useTypedSelector((state) =>
    getSuiteConfiguration(state)
  );

  const { armStatus, moduleArms } = getModuleArms(modules, moduleCapabilities);
  const [armsStatus, setArmsStatus] = useState(armStatus);
  const [movementParameter, setMovementParameter] = useState({
    iteration: testingState.movement?.parameter?.iteration ?? 1,
    tolerance: testingState.movement?.parameter?.tolerance ?? 0.3,
    carrierMovement: testingState.movement?.parameter?.carrierMovement ?? false,
    mfiFilterCubes: false,
  });

  const onClickArm = (id: string) => {
    const selectedIndex = armsStatus.findIndex((cell) => cell.id === id);
    let newArmList = armsStatus.slice();
    newArmList[selectedIndex].selected = !newArmList[selectedIndex].selected;
    setArmsStatus(newArmList);
  };

  const getSelectedModuleArm = () => {
    let result: ModuleArm[] = [];
    for (let i = 0; i < armsStatus.length; i++) {
      const arm = armsStatus[i];
      if (arm.selected) {
        const index = Number.parseInt(arm.id);
        result.push(moduleArms[index]);
      }
    }
    return result;
  };

  const runTest = () => {
    const arms = getSelectedModuleArm();
    const optionIsSequential = selectedOption == 1 ? true : false;
    TestingApi.doTesting({
      moduleIdentifier: "Suite",
      type: ETestingType.Movement,
      movementTest: {
        moduleArms: arms,
        iteration: movementParameter.iteration,
        tolerance: movementParameter.tolerance,
        carrierMovement: movementParameter.carrierMovement,
        isSequential: optionIsSequential,
        mfiFilterCubes: movementParameter.mfiFilterCubes,
      },
    });
  };

  const stopTest = () => {
    TestingApi.stopTesting();
  };

  const startEnabled =
    suiteMode !== ESuiteMode.Testing ||
    (suiteMode === ESuiteMode.Testing &&
      testingState.movement?.state === ETestingState.Stopped);

  const stopEnabled =
    suiteMode === ESuiteMode.Testing &&
    testingState.movement?.state === ETestingState.Started;

  const armOrCarrierSelected =
    movementParameter.carrierMovement ||
    armsStatus.reduce(
      (prev: boolean, current: MultiSelectData) => prev || current.selected,
      false
    ) ||
    movementParameter.mfiFilterCubes;

  const [selectedOption, setSelectedOption] = useState(1);

  let dropDownIsSequential: CommonDropDownDisplayValue<number>[] = [
    {
      display: "Sequentially Movement",
      value: 1,
      disabled: false,
    },
    {
      display: "Simultaneously Movement",
      value: 0,
      disabled: false,
    },
  ];

  const onCarrierChanged = (isSequential: number) => {
    setSelectedOption(isSequential);
  };

  const isImagerMFI = imagerType === EImagerType.RI360_MFI;

  return (
    <div className="testing-main-content">
      <div className="testing-content">
        <div className="testing-movement-options">
          <div className="testing-horizontal-items">
            <label style={{ marginRight: "30px" }}>Testing Option</label>
            <div style={{ marginRight: "10px" }}>:</div>
            <CommonDropDown
              id="testing-option-selector"
              value={selectedOption}
              style={{
                height: 24,
                width: 400,
                marginRight: "15px",
              }}
              onChange={(value) => onCarrierChanged(value)}
              dropDownDisplayValues={dropDownIsSequential}
            />
          </div>
          <div className="testing-horizontal-items">
            <label style={{ marginRight: "20px" }}>
              Testing Iteration (
              {dropDownIsSequential.filter((x) => x.value == selectedOption)[0]
                .value == 1
                ? "times"
                : "minutes"}
              )
            </label>
            <div style={{ marginRight: "10px" }}>:</div>
            <SpinnerTextBox
              min={0}
              max={100000}
              value={movementParameter.iteration}
              onValueChanged={(value) => {
                setMovementParameter({
                  ...movementParameter,
                  iteration: value,
                });
              }}
              style={{ height: 24, width: 180 }}
              disabled={isDisabled}
            />
          </div>
        </div>
        <div className="testing-movement-options">
          <div className="testing-horizontal-items">
            {armsStatus.map((arm) => (
              <div key={arm.id} style={{ marginRight: 30 }}>
                <CheckBox
                  id={arm.id}
                  name={arm.id + "-checkbox"}
                  label={arm.display}
                  checked={arm.selected}
                  onChange={(id) => onClickArm(id)}
                  colorVariant="alternate"
                  style={{ fontSize: 14, marginRight: 15 }}
                  disabled={isDisabled}
                />
              </div>
            ))}
            <div>
              <CheckBox
                id={"testint-movemet-carrier-checkbox"}
                name={"testint-movemet-carrier-checkbox"}
                label={"Carrier Movement"}
                checked={movementParameter.carrierMovement}
                onChange={() =>
                  setMovementParameter({
                    ...movementParameter,
                    carrierMovement: !movementParameter.carrierMovement,
                  })
                }
                colorVariant="alternate"
                style={{ fontSize: 14, marginRight: 15 }}
                disabled={isDisabled}
              />
            </div>
            {isImagerMFI && (
              <div>
                <CheckBox
                  id="testing-movement-mfi-filter-checkbox"
                  name="testing-movement-mfi-filter-checkbox"
                  label="MFI Filter Motor"
                  checked={movementParameter.mfiFilterCubes}
                  onChange={(id, checked) =>
                    setMovementParameter((prev) => ({
                      ...prev,
                      mfiFilterCubes: checked,
                    }))
                  }
                  colorVariant="alternate"
                  style={{ fontSize: 14, marginRight: 15 }}
                  disabled={isDisabled}
                />
              </div>
            )}
          </div>
          <div className="testing-horizontal-items">
            <label style={{ marginRight: "20px" }}>
              Diagnostic Tolerance (mm)
            </label>
            <div style={{ marginRight: "10px" }}>:</div>
            <TextBox
              name="testing-barcode-diag-tolerance"
              value={movementParameter.tolerance}
              isInteger={false}
              decimalPoint={2}
              onChange={(value) => {
                setMovementParameter({
                  ...movementParameter,
                  tolerance: value as number,
                });
              }}
              style={{ height: 24, width: 180, border: "1px solid #000000" }}
              disabled={isDisabled}
            />
          </div>
        </div>
        <div className="testing-button-group" style={{ marginTop: 0 }}>
          <ToolsContentButton
            text="Stop"
            onClick={stopTest}
            disabled={!stopEnabled}
            variant="primary"
            colorVariant="black"
          />
          <ToolsContentButton
            text="run"
            onClick={runTest}
            disabled={!startEnabled || !armOrCarrierSelected}
            variant="primary"
            colorVariant="default"
          />
        </div>
        <div
          className={"testing-status-box"}
          style={{ opacity: isDisabled ? 0.5 : 1 }}
        >
          {selectedOption == 1 && (
            <p>{"Counter: " + testingState.movement?.counter}</p>
          )}
          {selectedOption == 0 && (
            <p>
              {"Counter Imager Arm: " + testingState.movement?.counterImagerArm}
              <br></br>
              {"Counter PSR Arm: " + testingState.movement?.counterPsrArm}
              <br></br>
              {"Counter Carrier: " + testingState.movement?.counterCarrier}
              {isImagerMFI && (
                <>
                  <br></br>
                  {"MFI Filter Motor: " +
                    testingState.movement?.counterMfiFilterCubes}
                </>
              )}
            </p>
          )}
          <p>Diagnostic Status: </p>
          <pre>{testingState.movement?.status}</pre>
        </div>
      </div>
    </div>
  );
}
